/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    createMaterial,
    deleteMaterial,
    editMaterialDatabaseEntry,
    editMaterialDatabaseEntryByKey,
    getMaterialsDatabase,
} from "./materialsFunctions";
import { Effect, Material, MaterialDraft, Nullable, VersionedPagination } from "../../utils/Interfaces";

export function useMaterialsDatabase() {
    return useQuery({
        queryKey: ["OFFICE", "MATERIALS_DATABASE"],
        queryFn: getMaterialsDatabase,
        select: ({ data }) => {
            let processed: VersionedPagination<Material> = { ...data };
            processed.results = data.results.map((material: Material) => {
                let processedMaterial: Material = { ...material };
                processedMaterial.markup *= 100;
                return processedMaterial;
            });
            return processed;
        },
    });
}

function correctCachedValues(input: Material[]): Material[] {
    return input.map((material: Material) => {
        let processedMaterial: Material = { ...material };
        processedMaterial.markup /= 100;
        return processedMaterial;
    });
}

export function useCreateMaterialDatabaseEntry(onSuccess: Effect) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ([material, materials]: [MaterialDraft, Material[]]) => {
            queryClient.setQueryData(["OFFICE", "MATERIALS_DATABASE"], {
                data: { results: correctCachedValues(materials), count: materials.length },
            });
            return createMaterial(material);
        },
        onSuccess: onSuccess,
        onSettled: () => queryClient.invalidateQueries({ queryKey: ["OFFICE", "MATERIALS_DATABASE"] }),
    });
}

export function useEditMaterialDatabaseEntry(materialId: number, onSuccess: Effect) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ([material, materials]: [MaterialDraft, Material[]]) => {
            queryClient.setQueryData(["OFFICE", "MATERIALS_DATABASE"], {
                data: { results: correctCachedValues(materials), count: materials.length },
            });
            return editMaterialDatabaseEntry(materialId, material);
        },
        onSuccess: onSuccess,
        onSettled: () => queryClient.invalidateQueries({ queryKey: ["OFFICE", "MATERIALS_DATABASE"] }),
    });
}

export function useEditMaterialDatabaseEntryByKey(materialId: number, onSuccess: Effect) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ([updateKey, newValue, materials]: [keyof MaterialDraft, Nullable<string | number>, Material[]]) => {
            queryClient.setQueryData(["OFFICE", "MATERIALS_DATABASE"], {
                data: { results: correctCachedValues(materials), count: materials.length },
            });
            return editMaterialDatabaseEntryByKey(materialId, updateKey, newValue);
        },
        onSuccess: onSuccess,
        onSettled: () => queryClient.invalidateQueries({ queryKey: ["OFFICE", "MATERIALS_DATABASE"] }),
    });
}

export function useDeleteMaterialDatabaseEntry(materialId: number, onSuccess: Effect) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ([materials]: [Material[]]) => {
            queryClient.setQueryData(["OFFICE", "MATERIALS_DATABASE"], {
                data: { results: correctCachedValues(materials), count: materials.length },
            });
            return deleteMaterial(materialId);
        },
        onSuccess: onSuccess,
        onSettled: () => queryClient.invalidateQueries({ queryKey: ["OFFICE", "MATERIALS_DATABASE"] }),
    });
}
